import { useRef, useState } from "react";
import { tWithKeyAttr as t } from "../../i18nHelper";
import i18n from "../../i18n";
import BackArrowButton from "../backArrowButton/backArrowButton";
import { IUserReport } from "../../services/types/users.types";
import { useUploadFilesForClientMutation } from "../../services/api/user.api";
import styles from "./userDocuments.module.css";
import ClientFiles from "../clientFiles/clientFiles";

const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/jpeg",
  "image/png",
];

const ACCEPTED_EXTENSIONS = ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png";
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

type UploadError = { file: string; reason: string };

export type UserDocumentsProps = {
  onClose?: () => void;
  user: IUserReport;
};

function UserDocuments({ onClose, user }: UserDocumentsProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [subject, setSubject] = useState("");
  const [urgency, setUrgency] = useState<string>("");
  const [details, setDetails] = useState("");
  const [validationErrors, setValidationErrors] = useState<UploadError[]>([]);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadFiles, { isLoading, isError, reset }] = useUploadFilesForClientMutation();

  const validateAndAdd = (incoming: FileList | File[]): UploadError[] => {
    const errors: UploadError[] = [];
    const valid: File[] = [];

    Array.from(incoming).forEach((file) => {
      if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
        errors.push({ file: file.name, reason: t("DOCUMENTS_INVALID_FILE_TYPE") });
      } else if (file.size > MAX_FILE_SIZE_BYTES) {
        errors.push({ file: file.name, reason: t("DOCUMENTS_FILE_TOO_LARGE") });
      } else {
        valid.push(file);
      }
    });

    setFiles((prev) => [...prev, ...valid]);
    return errors;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const errors = validateAndAdd(e.target.files);
    setValidationErrors(errors);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (!e.dataTransfer.files.length) return;
    const errors = validateAndAdd(e.dataTransfer.files);
    setValidationErrors(errors);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!files.length) return;
    reset();
    setUploadedUrls([]);
    setValidationErrors([]);

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    if (subject.trim()) formData.append("subject", subject.trim());
    if (urgency !== "") formData.append("urgency", urgency);
    if (details.trim()) formData.append("details", details.trim());

    const result = await uploadFiles({ patientUserGuid: user.Users.Guid, formData });
    if ("data" in result && result.data.success) {
      setUploadedUrls(result.data.urls ?? []);
      setFiles([]);
      setSubject("");
      setUrgency("");
      setDetails("");
    }
  };

  return (
    <section className={styles.section} dir={i18n.dir()}>
      <BackArrowButton text={t("PREVIOUS_SCREEN")} onClick={onClose} position={"left"} />
      <h3 className={styles.title}>{t("DOCUMENTS_TITLE")}</h3>

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{t("DOCUMENTS_UPLOAD_TITLE")}</h2>

        {/* Drop zone */}
        <div
          className={`${styles.dropZone} ${isDragging ? styles.dropZoneActive : ""}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <span className={styles.dropZoneIcon}>📎</span>
          <p className={styles.dropZoneText}>{t("DOCUMENTS_DROP_ZONE")}</p>
          <p className={styles.dropZoneHint}>{t("DOCUMENTS_ACCEPTED_FORMATS")}</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_EXTENSIONS}
            className={styles.hiddenInput}
            onChange={handleFileChange}
          />
        </div>

        {/* Selected files */}
        {files.length > 0 && (
          <ul className={styles.fileList}>
            {files.map((file, i) => (
              <li key={i} className={styles.fileItem}>
                <span className={styles.fileName}>{file.name}</span>
                <span className={styles.fileSize}>
                  ({(file.size / 1024).toFixed(0)} KB)
                </span>
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={() => removeFile(i)}
                  title={t("CANCEL")}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Validation errors */}
        {validationErrors.length > 0 && (
          <ul className={styles.errorList}>
            {validationErrors.map((err, i) => (
              <li key={i} className={styles.errorItem}>
                <strong>{err.file}</strong>: {err.reason}
              </li>
            ))}
          </ul>
        )}

        {/* Optional fields */}
        <div className={styles.fields}>
          <label className={styles.fieldLabel}>
            {t("DOCUMENTS_SUBJECT")}
            <input
              type="text"
              className={styles.input}
              value={subject}
              maxLength={200}
              onChange={(e) => setSubject(e.target.value)}
            />
          </label>

          <label className={styles.fieldLabel}>
            {t("DOCUMENTS_URGENCY")}
            <select
              className={styles.input}
              value={urgency}
              onChange={(e) => setUrgency(e.target.value)}
            >
              <option value="">—</option>
              <option value="1">1 – {t("DOCUMENTS_URGENCY_LOW")}</option>
              <option value="2">2</option>
              <option value="3">3 – {t("DOCUMENTS_URGENCY_MEDIUM")}</option>
              <option value="4">4</option>
              <option value="5">5 – {t("DOCUMENTS_URGENCY_HIGH")}</option>
            </select>
          </label>

          <label className={`${styles.fieldLabel} ${styles.fieldLabelFull}`}>
            {t("DOCUMENTS_DETAILS")}
            <textarea
              className={`${styles.input} ${styles.textarea}`}
              value={details}
              maxLength={1000}
              rows={3}
              onChange={(e) => setDetails(e.target.value)}
            />
          </label>
        </div>

        {/* API error */}
        {isError && (
          <p className={styles.apiError}>{t("DOCUMENTS_UPLOAD_ERROR")}</p>
        )}

        {/* Success URLs */}
        {uploadedUrls.length > 0 && (
          <div className={styles.successBlock}>
            <p className={styles.successMsg}>{t("DOCUMENTS_UPLOAD_SUCCESS")}</p>
            <ul className={styles.urlList}>
              {uploadedUrls.map((url, i) => (
                <li key={i}>
                  <a href={url} target="_blank" rel="noopener noreferrer" className={styles.urlLink}>
                    {url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <button
          type="button"
          className={styles.uploadBtn}
          disabled={files.length === 0 || isLoading}
          onClick={handleSubmit}
        >
          {isLoading ? t("LOADING") : t("DOCUMENTS_UPLOAD_BUTTON")}
        </button>
      </div>

      <ClientFiles user={user.Users} />
    </section>
  );
}

export default UserDocuments;
